import { darken, decomposeColor, getContrastRatio, hslToRgb } from '@mui/material/styles'

export const statusAlertTint = { info: .1, success: .1, warning: .12, error: .1 } as const
type StatusSeverity = keyof typeof statusAlertTint

// Leave a little room above 4.5 for rounding in CSS and MUI's luminance calculation.
const minimumContrast = 4.6
const fallbackDark = '#1f2933'
const rgb = (values: number[]) => `rgb(${values.map(value => Math.round(value)).join(', ')})`

const readColor = (value: string) => {
  try {
    const normalized = value.trim()
    const color = decomposeColor(/^hsla?\(/.test(normalized) ? hslToRgb(normalized) : normalized)
    // CSS variables, named colors, gradients and wide-gamut colors cannot be
    // resolved reliably during SSR. Keep their configured background, using a
    // dark foreground that is safe on the existing 88–90% white alert tint.
    if (!['rgb', 'rgba'].includes(color.type) || /rgba?\([^)]*%/.test(normalized)) return null
    const [red, green, blue, alpha = 1] = color.values
    if (![red, green, blue, alpha].every(Number.isFinite)
      || [red, green, blue].some(channel => channel < 0 || channel > 255)
      || alpha < 0 || alpha > 1) return null
    return { channels: [red, green, blue], alpha }
  } catch {
    return null
  }
}

const readableDarkColor = (color: string, background: string) => {
  let result = color
  for (let step = 0; step < 20 && getContrastRatio(result, background) < minimumContrast; step += 1) {
    result = darken(result, .1)
  }
  return getContrastRatio(result, background) >= minimumContrast ? result : fallbackDark
}

/** Preserve the badge hue, darkening only when white text needs more contrast. */
export const badgeBackground = (themeColor: string) => {
  const color = readColor(themeColor)
  if (!color) return fallbackDark
  const background = color.alpha === 1 ? themeColor
    : rgb(color.channels.map(channel => channel * color.alpha + 255 * (1 - color.alpha)))
  return readableDarkColor(background, '#fff')
}

/** Readable text for the existing color-mix(status, white) alert background. */
export const statusAlertText = (themeColor: string, severity: StatusSeverity) => {
  const color = readColor(themeColor)
  if (!color) return fallbackDark
  const tint = statusAlertTint[severity]
  // For translucent colors use the darkest possible backing surface. This also
  // covers non-white page themes without changing their configured backgrounds.
  const backgroundChannels = color.channels.map(channel => 255 * (1 - tint) + channel * tint * color.alpha)
  const foreground = color.alpha === 1 ? themeColor
    : rgb(color.channels.map((channel, index) => channel * color.alpha + backgroundChannels[index] * (1 - color.alpha)))
  return readableDarkColor(foreground, rgb(backgroundChannels))
}
